import { Box, Button, ButtonGroup, Stack, Typography, useTheme } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import React, { Dispatch, SetStateAction } from 'react';
import ReactApexChart from 'react-apexcharts';

interface FundAllocationGraphProps {
  data: Record<string, number>; // Accepts dynamic data
  setCategory: Dispatch<SetStateAction<string>>;
  selected: string;
}

const FundAllocationGraph: React.FC<FundAllocationGraphProps> = ({ data = {}, setCategory, selected }) => {
  const theme = useTheme();

  const buttonOptions = [
    {
      label: 'Sector',
      value: 'SECTOR',
    },
    {
      label: 'Country',
      value: 'COUNTRY',
    },
    {
      label: 'Currency',
      value: 'CURRENCY',
    },
  ];

  // Ensure data is always an object
  const validData = data && typeof data === 'object' ? data : {};

  // Process data to get series and total value
  const processedData = React.useMemo(() => {
    const total = Object?.values(validData)?.reduce((sum, val) => sum + val, 0);
    const series = Object?.values(validData)?.map((value) => ((value / total) * 100).toFixed(1));

    return {
      series: series.map(Number),
      labels: Object?.keys(validData),
      totalValue: total,
      holdings: Object?.entries(validData).map(([name, value]) => ({
        name,
        value,
      })),
    };
  }, [validData]);

  const colors = [
    // Original Colors
    '#9333EA', // Purple
    '#818CF8', // Indigo
    '#38BDF8', // Light Blue
    '#34D399', // Emerald
    '#FB923C', // Orange
    '#4F46E5', // Indigo
    '#06B6D4', // Cyan
    '#22C55E', // Green
    '#F59E0B', // Amber
    '#EC4899', // Pink
    '#7C3AED', // Violet
    '#2DD4BF', // Teal
    '#F43F5E', // Rose
    '#14B8A6', // Teal
    '#8B5CF6', // Purple
    '#EAB308', // Yellow
    '#3B82F6', // Blue
    '#10B981', // Emerald
    '#6366F1', // Indigo
    '#F97316', // Orange
    '#0EA5E9', // Sky
    '#D946EF', // Fuchsia
    '#84CC16', // Lime
    '#6D28D9', // Purple
    '#0891B2', // Cyan
  ];

  const options: ApexOptions = {
    chart: {
      type: 'donut',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '80%',
          labels: {
            show: true,
            total: {
              showAlways: true,
              show: true,
              label: 'Total Value',
              color: theme?.palette?.text?.primary,
              formatter: () =>
                `£${processedData.totalValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`,
            },
          },
        },
      },
    },
    colors: colors,
    stroke: {
      show: false,
    },
    labels: processedData.labels,
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (_, { seriesIndex }) => {
          const amount = processedData.holdings[seriesIndex]?.value ?? 0;
          return `£${amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 300,
          },
        },
      },
    ],
  };

  return (
    <Stack spacing={2.5}>
      {processedData.series.length > 0 ? (
        <>
          <ButtonGroup
            variant="text"
            size="small"
            color="inherit"
            sx={{
              mt: 2.5,
              '& .MuiButtonGroup-grouped': {
                height: '24px',
                fontSize: '16px',
                paddingLeft: '6px',
                paddingRight: '6px',
                fontWeight: 400,
                color: 'text.primary',
                borderColor: `${theme.palette.text.primary} !important`,
              },
            }}
          >
            {buttonOptions?.map((e) => (
              <Button sx={{ textDecoration: selected === e.value ? 'underline' : 'unset', textUnderlineOffset: '5px' }} onClick={() => setCategory(e.value)}>
                {e.label}
              </Button>
            ))}
          </ButtonGroup>
          <ReactApexChart options={options} series={processedData.series} type="donut" height={350} />
          <Stack gap={1}>
            <Stack direction="row" justifyContent="center" alignItems="center" flexWrap="wrap" gap={2}>
              {processedData.holdings.map((holding, index) => {
                return (
                  <>
                    {holding.value > 0 && (
                      <Stack direction="row" alignItems="flex-start" key={holding.name}>
                        <Box
                          component="span"
                          mr={1}
                          sx={{
                            display: 'block',
                            backgroundColor: colors[index % colors.length],
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            mt: 0.8,
                          }}
                        ></Box>
                        <Box sx={{ display: 'block' }} component="span">
                          <Typography variant="subtitle2" color="text.secondary">
                            {holding.name}
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </>
                );
              })}
            </Stack>
          </Stack>
        </>
      ) : (
        <Stack minHeight="20vh" sx={{ alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="subtitle2">No data available</Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default FundAllocationGraph;
